// src/services/AuthService.js
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updatePassword as firebaseUpdatePassword,
  updateProfile,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  linkWithCredential,
  unlink,
  deleteUser,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth, db, storage } from '../firebase';

class AuthService {
  static instance = null;

  constructor() {
    if (AuthService.instance) {
      return AuthService.instance;
    }
    AuthService.instance = this;
    this.auth = auth;
    this.db = db;
  }

  // MARK: - Sign Up

  async createAccount(email, password, firstName, lastName = null) {
    try {
      const authResult = await createUserWithEmailAndPassword(this.auth, email, password);
      const user = authResult.user;

      // Update user profile with display name
      const fullName = lastName && lastName.trim() !== '' ? `${firstName} ${lastName}` : firstName;
      await updateProfile(user, {
        displayName: fullName,
      });

      // Store private user data in Firestore /users collection
      const privateUserData = {
        improveModelForEveryone: true,
        createdAt: serverTimestamp(),
      };

      await setDoc(doc(this.db, 'users', user.uid), privateUserData);

      // Store public profile data in Firestore /profiles collection
      const publicProfileData = {
        firstName: firstName,
        email: email,
      };

      if (lastName && lastName.trim() !== '') {
        publicProfileData.lastName = lastName;
      }

      await setDoc(doc(this.db, 'profiles', user.uid), publicProfileData);

      console.log('✅ Account created successfully');
      return user;
    } catch (error) {
      console.error('Error creating account:', error);
      throw error;
    }
  }

  // MARK: - Sign In

  async signIn(email, password) {
    try {
      const authResult = await signInWithEmailAndPassword(this.auth, email, password);
      console.log('✅ Signed in successfully');
      return authResult.user;
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  }

  // MARK: - Google Sign In

  async signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(this.auth, provider);
      const user = result.user;

      // Extract user profile information from Google
      const googleProfile = result.user.providerData.find(p => p.providerId === 'google.com');
      const firstName = googleProfile?.displayName?.split(' ')[0] || '';
      const lastName = googleProfile?.displayName?.split(' ').slice(1).join(' ') || null;
      const email = user.email;

      // Check if this is a new user
      const isNewUser = result.additionalUserInfo?.isNewUser || false;

      if (isNewUser) {
        // Store private user data in Firestore /users collection
        const privateUserData = {
          improveModelForEveryone: true,
          createdAt: serverTimestamp(),
          authProvider: 'google',
        };

        await setDoc(doc(this.db, 'users', user.uid), privateUserData);

        // Store public profile data in Firestore /profiles collection
        const publicProfileData = {
          firstName: firstName || '',
          email: email,
          needsProfileCompletion: !firstName,
        };

        if (lastName) {
          publicProfileData.lastName = lastName;
        }

        if (user.photoURL) {
          publicProfileData.profilePhotoURL = user.photoURL;
        }

        await setDoc(doc(this.db, 'profiles', user.uid), publicProfileData);

        console.log('✅ Created new Google user profile');
      }

      console.log('✅ Signed in with Google successfully');
      return user;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  }

  // MARK: - Sign Out

  async signOut() {
    try {
      await firebaseSignOut(this.auth);
      console.log('✅ Signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // MARK: - Password Reset

  async sendPasswordReset(email) {
    try {
      await sendPasswordResetEmail(this.auth, email);
      console.log('✅ Password reset email sent');
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw error;
    }
  }

  // MARK: - Change Password

  async reauthenticate(currentPassword) {
    const user = this.auth.currentUser;
    if (!user || !user.email) {
      throw new Error('No user signed in');
    }

    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
  }

  async updatePassword(newPassword) {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('No user signed in');
    }

    await firebaseUpdatePassword(user, newPassword);
    console.log('✅ Password updated successfully');
  }

  // MARK: - Provider Linking/Unlinking

  async addPasswordAuthentication(password) {
    const user = this.auth.currentUser;
    if (!user || !user.email) {
      throw new Error('No user signed in');
    }

    // Check if user already has password provider
    const hasPasswordProvider = user.providerData.some(p => p.providerId === 'password');
    if (hasPasswordProvider) {
      throw new Error('Password authentication already exists');
    }

    const credential = EmailAuthProvider.credential(user.email, password);
    await linkWithCredential(user, credential);

    console.log('✅ Successfully added password authentication');
  }

  async linkGoogleAuthentication() {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('No user signed in');
    }

    // Check if user already has Google provider
    const hasGoogleProvider = user.providerData.some(p => p.providerId === 'google.com');
    if (hasGoogleProvider) {
      throw new Error('Google authentication already linked');
    }

    const provider = new GoogleAuthProvider();
    await linkWithCredential(user, provider);

    console.log('✅ Successfully linked Google authentication');
  }

  async unlinkProvider(providerId) {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('No user signed in');
    }

    // Check that the provider is actually linked
    const hasProvider = user.providerData.some(p => p.providerId === providerId);
    if (!hasProvider) {
      throw new Error('Provider not linked to this account');
    }

    // Ensure user has at least one other provider after unlinking
    if (user.providerData.length <= 1) {
      throw new Error('Cannot unlink the only sign-in method. Please add another method first.');
    }

    await unlink(user, providerId);
    console.log(`✅ Successfully unlinked ${providerId}`);
  }

  // MARK: - Delete Account

  async deleteAccount() {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('No user signed in');
    }

    const uid = user.uid;

    try {
      // Delete private user data from Firestore /users collection
      await deleteDoc(doc(this.db, 'users', uid));

      // Delete public profile data from Firestore /profiles collection
      await deleteDoc(doc(this.db, 'profiles', uid));

      // Delete Firebase Auth account
      await deleteUser(user);

      console.log('✅ Account deleted successfully');
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  }

  // MARK: - User Profile

  async updateUserProfile(firstName, lastName = null, improveModelForEveryone = true) {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('No user signed in');
    }

    // Update Firebase Auth display name
    const fullName = lastName && lastName.trim() !== '' ? `${firstName} ${lastName}` : firstName;
    await updateProfile(user, {
      displayName: fullName,
    });

    // Update private user data in /users collection
    const privateUpdateData = {
      improveModelForEveryone: improveModelForEveryone,
    };

    await updateDoc(doc(this.db, 'users', user.uid), privateUpdateData);

    // Update public profile data in /profiles collection
    const publicUpdateData = {
      firstName: firstName,
    };

    if (lastName && lastName.trim() !== '') {
      publicUpdateData.lastName = lastName;
    }

    await updateDoc(doc(this.db, 'profiles', user.uid), publicUpdateData);

    console.log('✅ Profile updated successfully');
  }

  async getUserData() {
    const user = this.auth.currentUser;
    if (!user) {
      return null;
    }

    const docSnap = await getDoc(doc(this.db, 'users', user.uid));
    return docSnap.exists() ? docSnap.data() : null;
  }

  async getPublicProfile(uid = null) {
    const userId = uid || this.auth.currentUser?.uid;
    if (!userId) {
      return null;
    }

    const docSnap = await getDoc(doc(this.db, 'profiles', userId));
    return docSnap.exists() ? docSnap.data() : null;
  }

  // Check if profile exists in Firestore by email
  // This matches the Swift implementation: userRepository.profileExists(for: email)
  async profileExists(email) {
    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');

      // Query the profiles collection for a document with matching email
      const profilesRef = collection(this.db, 'profiles');
      const q = query(profilesRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);

      // If we find any profiles with this email, the account exists
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking if profile exists:', error);
      // If there's an error (like permission denied), we can't determine
      // so we return false to allow account creation flow
      return false;
    }
  }

  // MARK: - Profile Photo

  async uploadProfilePhoto(file) {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('No user signed in');
    }

    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      // Resize and compress the image
      const resizedImage = await this.resizeImage(file, 1024);

      // Upload to Firebase Storage at profilePhotos/{userId}.jpg
      const storageRef = ref(storage, `profilePhotos/${user.uid}.jpg`);
      const metadata = {
        contentType: 'image/jpeg',
      };

      await uploadBytes(storageRef, resizedImage, metadata);

      // Get the download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update the profile in Firestore
      await updateDoc(doc(this.db, 'profiles', user.uid), {
        profilePhotoURL: downloadURL,
      });

      console.log('✅ Profile photo uploaded successfully');
      return downloadURL;
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      throw error;
    }
  }

  async deleteProfilePhoto() {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('No user signed in');
    }

    try {
      // Delete from Firebase Storage
      const storageRef = ref(storage, `profilePhotos/${user.uid}.jpg`);
      try {
        await deleteObject(storageRef);
      } catch (error) {
        // Ignore error if file doesn't exist
        if (error.code !== 'storage/object-not-found') {
          throw error;
        }
      }

      // Remove from Firestore profile
      await updateDoc(doc(this.db, 'profiles', user.uid), {
        profilePhotoURL: null,
      });

      console.log('✅ Profile photo deleted successfully');
    } catch (error) {
      console.error('Error deleting profile photo:', error);
      throw error;
    }
  }

  // Helper function to resize and compress image
  async resizeImage(file, maxDimension) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Calculate new dimensions
          const aspect = img.width / img.height;
          let width, height;
          if (aspect > 1) {
            width = maxDimension;
            height = maxDimension / aspect;
          } else {
            width = maxDimension * aspect;
            height = maxDimension;
          }

          canvas.width = width;
          canvas.height = height;

          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            0.7 // 70% quality
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  // Get current user
  get currentUser() {
    return this.auth.currentUser;
  }

  get isAuthenticated() {
    return this.auth.currentUser !== null;
  }

  get currentUID() {
    return this.auth.currentUser?.uid || null;
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
